import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PhoneInput, {
  formatPhoneNumberIntl,
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "@/styles/phone-input.css";
import {
  CheckCircle2,
  Lock,
  Upload,
  ShoppingBag,
  Factory,
} from "lucide-react";
import PageHero from "@/components/PageHero";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INGREDIENT_FORMS = [
  "Powder",
  "Liquid",
  "Extract",
  "Oil",
  "Granular",
  "Crystalline",
  "Other",
];

const APPLICATIONS = [
  "Nutraceuticals / Supplements",
  "Food & Beverage",
  "Animal Feed",
  "Personal Care",
  "Functional / Specialty",
  "Other",
];

const CERTIFICATIONS = [
  "Organic",
  "Non-GMO",
  "Kosher",
  "Halal",
  "GMP",
  "ISO",
  "FSSC 22000",
  "Other / Multiple",
];

const BUYER_GOALS = [
  "New supplier discovery",
  "Procurement consolidation",
  "Cross-border validation",
  "Alternative / backup sourcing",
  "Cost & spec optimization",
  "Not sure yet",
];

const BUYER_PROGRAM = [
  "Sourcing intelligence report",
  "Verified supplier shortlist",
  "Warm introductions",
  "All of the above",
  "Not sure — recommend for me",
];

const SUPPLIER_GOALS = [
  "Enter a new geographic market",
  "Reach specific buyer programs",
  "Differentiate from similar offers",
  "Test demand for a new product",
  "Build long-term buyer presence",
  "Not sure yet",
];

const SUPPLIER_PROGRAM = [
  "Supplier representation",
  "Buyer opportunity briefs",
  "Warm introductions",
  "All of the above",
  "Not sure — recommend for me",
];

// "Category playbook" — concrete examples of what a rich, actionable sourcing
// request looks like. Shown inline under each Get Started field so buyers know
// what level of specificity helps analysts respond faster.
const BUYER_EXAMPLES = {
  ingredient_category:
    "Ashwagandha extract, 5% withanolides (KSM-66 or Sensoril grade acceptable)",
  ingredient_form: "Water-soluble spray-dried powder, mesh 80–120",
  application_area:
    "Nutraceutical stick-packs — needs cold-water dispersibility",
  geography:
    "Preferring India (Rajasthan / MP belt); open to Sri Lanka as backup",
  volume_range: "8 MT in year one, growing to ~20 MT by year three",
  timeline: "Q1 next year — pilot 500 kg, commercial by month 6",
  certifications:
    "Organic (USDA & EU) mandatory; ISO 22000 preferred; heavy-metal COAs required",
  primary_goal:
    "Consolidate 3 existing regional suppliers into 1–2 vetted global partners",
  program_interest:
    "Verified supplier shortlist with pricing + lead-time benchmarking",
  additional_notes:
    "Current supplier is 12-week lead time — we need 6–8 weeks. Willing to pay a premium for guaranteed dispatch windows. Please match this spec sheet: [link/attach].",
};

const SUPPLIER_EXAMPLES = {
  ingredient_category:
    "Grass-fed bovine collagen peptides, DH 15–25, average MW 3 kDa",
  ingredient_form: "Fine powder, agglomerated for cold-water solubility",
  application_area: "Ready-to-mix beverages and gummies",
  markets_served:
    "Currently EU + GCC; targeting entry into North America and Japan next",
  capacity_range:
    "1,200 MT/year installed, running at ~65% utilisation — room for ~400 MT of new demand",
  moq: "500 kg for stocked SKUs; 2 MT for custom blends",
  certifications:
    "Halal, Kosher, ISO 22000, FSSC 22000 — Organic in progress (audit Q3)",
  primary_goal:
    "Enter North American nutraceutical market via 1–2 category-focused distributors",
  program_interest:
    "Verified market-entry programme with distributor introductions",
  additional_notes:
    "Ten years supplying tier-2 European brands. Happy to share three anonymised case studies + a plant-audit video call.",
};

const blankForm = {
  audience: "buyer",
  full_name: "",
  role: "",
  company_name: "",
  work_email: "",
  phone: "",
  ingredient_category: "",
  ingredient_form: "",
  application_area: "",
  geography: "",
  markets_served: "",
  volume_range: "",
  capacity_range: "",
  moq: "",
  certifications: "",
  timeline: "",
  primary_goal: "",
  program_interest: "",
  additional_notes: "",
  consent: false,
};

const CONSENT_TEXT =
  "I consent to FFIB/IngredientSignal contacting me about sourcing and following up on my enquiry.";

const inputCls =
  "bg-brand-ivory border border-brand-border text-brand-deep px-4 py-3 text-sm focus:outline-none focus:border-brand-olive focus:ring-1 focus:ring-brand-olive transition-colors placeholder:text-brand-quiet/70 font-light w-full";

const Field = ({ label, htmlFor, required, hint, example, error, children }) => (
  <div className="flex flex-col gap-2">
    <label
      htmlFor={htmlFor}
      className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-muted"
    >
      {label} {required && <span className="text-brand-olive">*</span>}
    </label>
    {children}
    {error && <span className="text-xs text-red-700">{error}</span>}
    {hint && !error && (
      <p className="text-xs text-brand-quiet font-light">{hint}</p>
    )}
    {example && !error && (
      <p
        className="text-[11px] text-brand-quiet/90 italic leading-relaxed pl-2 border-l-2 border-brand-olive/30"
        data-testid={`field-example-${htmlFor}`}
      >
        <span className="not-italic font-mono text-[10px] tracking-[0.18em] uppercase text-brand-olive mr-1.5">
          e.g.
        </span>
        {example}
      </p>
    )}
  </div>
);

const SectionTitle = ({ index, children }) => (
  <div className="flex items-baseline gap-4 pt-4">
    <span className="font-mono text-xs text-brand-olive tracking-[0.24em]">
      {String(index).padStart(2, "0")}
    </span>
    <h3 className="font-display text-xl text-brand-deep font-medium">{children}</h3>
  </div>
);

// Small callout that primes users on how much detail helps the FFIB analysts.
// Rendered directly under the section title of the Sourcing Need / Your Offering
// blocks — the fields that most benefit from richer answers.
const PlaybookCallout = ({ audience }) => (
  <div
    data-testid={`playbook-callout-${audience}`}
    className="rounded-xl border border-brand-olive/25 bg-brand-linen/70 px-5 py-4 flex items-start gap-3"
  >
    <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-olive/15 text-brand-olive border border-brand-olive/30 font-mono text-[10px] tracking-[0.14em]">
      i
    </span>
    <div className="text-sm text-brand-warm leading-relaxed">
      <span className="font-medium text-brand-deep">
        {audience === "buyer"
          ? "What a good sourcing request looks like."
          : "What a strong supplier profile looks like."}
      </span>{" "}
      Each field below has a short "e.g." underneath showing the level of detail
      that helps our analysts respond fast — grades, geography, volumes,
      timelines, deal-breakers. You don't have to answer every field, but the
      more specific you are, the better the shortlist you'll get back.
    </div>
  </div>
);

export default function GetStarted() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialAudience =
    searchParams.get("audience") === "supplier" ? "supplier" : "buyer";
  // Optional URL pre-fill: /get-started?audience=buyer&ingredient=Dandelion%20(Taraxacum%20officinale)
  // useSearchParams already URL-decodes; React value binding is XSS-safe (values
  // render as text, never as HTML). Trim + cap to the model's 200-char ceiling.
  const initialIngredient = (searchParams.get("ingredient") || "")
    .trim()
    .slice(0, 200);

  const [audience, setAudience] = useState(initialAudience);
  const [form, setForm] = useState({
    ...blankForm,
    audience: initialAudience,
    ingredient_category: initialIngredient,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Keep URL param in sync
  useEffect(() => {
    if (searchParams.get("audience") !== audience) {
      const next = new URLSearchParams(searchParams);
      next.set("audience", audience);
      setSearchParams(next, { replace: true });
    }
    setForm((f) => ({ ...f, audience }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  const isBuyer = audience === "buyer";
  const EX = isBuyer ? BUYER_EXAMPLES : SUPPLIER_EXAMPLES;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextVal = type === "checkbox" ? checked : value;
    setForm((prev) => ({ ...prev, [name]: nextVal }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.company_name.trim()) e.company_name = "Required";
    if (!form.work_email.trim()) e.work_email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email))
      e.work_email = "Enter a valid email";
    if (form.phone && !isValidPhoneNumber(form.phone))
      e.phone = "Enter a valid phone number for the selected country";
    if (!form.consent)
      e.consent =
        "Please confirm you consent to FFIB/IngredientSignal contacting you.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);

    // Build payload — strip empty strings to null for cleaner DB records
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [
        k,
        typeof v === "string" && v.trim() === "" ? null : v,
      ])
    );
    payload.audience = audience;
    payload.request_type =
      audience === "buyer" ? "buyer_application" : "supplier_application";
    // Send phone in international format ("+91 78780 16065") — nicer for
    // ISBackend Alerts than raw E.164 while remaining unambiguous.
    if (form.phone) payload.phone = formatPhoneNumberIntl(form.phone);

    try {
      await axios.post(`${API}/intake`, payload);
      setSubmitted(true);
      toast.success(
        isBuyer
          ? "Buyer application received. FFIB will be in touch."
          : "Supplier application received. FFIB will be in touch."
      );
      setForm({ ...blankForm, audience });
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "We couldn't submit your application right now. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = useMemo(
    () =>
      isBuyer ? "Submit Buyer Application" : "Submit Supplier Application",
    [isBuyer]
  );

  return (
    <div data-testid="get-started-page">
      <PageHero
        eyebrow="Apply"
        title={
          <>
            Tell us what
            <br />
            <span className="text-brand-olive">you need</span>.
          </>
        }
        subtitle="FFIB analysts review every application personally. No spam, no sales pitch — just an honest assessment of whether and how we can help. All information is kept strictly confidential."
        accent={
          <>
            <span className="block text-brand-deep font-medium mb-2">
              Confidential review
            </span>
            Submissions are read by FFIB analysts only. We typically respond
            within two business days.
          </>
        }
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {submitted ? (
            <div
              data-testid="intake-success-state"
              className="border border-brand-olive/40 bg-brand-ivory p-12 md:p-16"
            >
              <CheckCircle2
                className="w-10 h-10 text-brand-olive"
                strokeWidth={1.4}
              />
              <h2 className="font-display mt-8 text-3xl md:text-4xl text-brand-deep font-light leading-tight">
                Application received.
              </h2>
              <p className="mt-6 text-brand-warm max-w-2xl leading-relaxed font-light">
                Thank you. An FFIB analyst will review your submission and
                follow up at the email you provided. If your request is time
                sensitive, you can reach us directly at{" "}
                <a
                  href="mailto:agastya@ingredientsignal.com"
                  className="text-brand-olive hover:text-brand-olive transition-colors"
                  data-testid="success-email-link"
                >
                  agastya@ingredientsignal.com
                </a>
                .
              </p>
              <button
                type="button"
                data-testid="intake-submit-another"
                onClick={() => setSubmitted(false)}
                className="mt-10 inline-flex items-center gap-2 border border-brand-muted/40 hover:border-brand-muted/70 text-brand-deep px-6 py-3 text-sm font-medium transition-colors hover:bg-brand-border"
              >
                Submit another application
              </button>
            </div>
          ) : (
            <>
              {/* Audience toggle */}
              <div
                data-testid="audience-toggle"
                role="tablist"
                aria-label="Application type"
                className="inline-flex border border-brand-border bg-brand-linen p-1 mb-10"
              >
                {[
                  { key: "buyer", label: "I am a Buyer", Icon: ShoppingBag },
                  { key: "supplier", label: "I am a Supplier", Icon: Factory },
                ].map((opt) => {
                  const active = audience === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      data-testid={`audience-toggle-${opt.key}`}
                      onClick={() => setAudience(opt.key)}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium tracking-wide transition-colors ${
                        active
                          ? "bg-brand-olive text-brand-cream"
                          : "text-brand-warm hover:text-brand-deep"
                      }`}
                    >
                      <opt.Icon className="w-4 h-4" strokeWidth={1.5} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              <form
                data-testid="intake-form"
                onSubmit={handleSubmit}
                noValidate
                className="border border-brand-border bg-brand-linen p-8 md:p-12 space-y-12"
              >
                {/* About You */}
                <div className="space-y-6">
                  <SectionTitle index={1}>About You</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field
                      label="Full Name"
                      htmlFor="full_name"
                      required
                      error={errors.full_name}
                    >
                      <input
                        id="full_name"
                        name="full_name"
                        value={form.full_name}
                        onChange={handleChange}
                        autoComplete="name"
                        className={inputCls}
                        data-testid="intake-full-name"
                        placeholder="Jane Carter"
                      />
                    </Field>
                    <Field label="Role / Title" htmlFor="role">
                      <input
                        id="role"
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                        className={inputCls}
                        data-testid="intake-role"
                        placeholder="Head of Procurement"
                      />
                    </Field>
                    <Field
                      label="Work Email"
                      htmlFor="work_email"
                      required
                      error={errors.work_email}
                    >
                      <input
                        id="work_email"
                        name="work_email"
                        type="email"
                        value={form.work_email}
                        onChange={handleChange}
                        autoComplete="email"
                        className={inputCls}
                        data-testid="intake-work-email"
                        placeholder="jane@acmenutra.com"
                      />
                    </Field>
                    <Field
                      label="Company"
                      htmlFor="company_name"
                      required
                      error={errors.company_name}
                    >
                      <input
                        id="company_name"
                        name="company_name"
                        value={form.company_name}
                        onChange={handleChange}
                        autoComplete="organization"
                        className={inputCls}
                        data-testid="intake-company-name"
                        placeholder="Acme Nutraceuticals"
                      />
                    </Field>
                    <Field label="Phone" htmlFor="phone" error={errors.phone}>
                      <PhoneInput
                        id="phone"
                        name="phone"
                        international
                        defaultCountry="US"
                        countryCallingCodeEditable={false}
                        value={form.phone || undefined}
                        onChange={(val) => {
                          setForm((prev) => ({ ...prev, phone: val || "" }));
                          if (errors.phone)
                            setErrors((p) => ({ ...p, phone: undefined }));
                        }}
                        autoComplete="tel"
                        data-testid="intake-phone"
                        placeholder="Enter phone number"
                      />
                    </Field>
                  </div>
                </div>

                {/* Section 2 — Buyer: Sourcing Need / Supplier: Offering */}
                {isBuyer ? (
                  <div data-testid="buyer-section" className="space-y-6">
                    <SectionTitle index={2}>Sourcing Need</SectionTitle>
                    <PlaybookCallout audience="buyer" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Field
                        label="Ingredient / Category"
                        htmlFor="ingredient_category"
                        example={EX.ingredient_category}
                      >
                        <input
                          id="ingredient_category"
                          name="ingredient_category"
                          value={form.ingredient_category}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-ingredient-category"
                          placeholder="e.g. Ashwagandha extract, MCT powder"
                        />
                      </Field>
                      <Field
                        label="Form"
                        htmlFor="ingredient_form"
                        example={EX.ingredient_form}
                      >
                        <select
                          id="ingredient_form"
                          name="ingredient_form"
                          value={form.ingredient_form}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-ingredient-form"
                        >
                          <option value="">Select form</option>
                          {INGREDIENT_FORMS.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field
                        label="Application Area"
                        htmlFor="application_area"
                        example={EX.application_area}
                      >
                        <select
                          id="application_area"
                          name="application_area"
                          value={form.application_area}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-application-area"
                        >
                          <option value="">Select application</option>
                          {APPLICATIONS.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field
                        label="Target Sourcing Geography"
                        htmlFor="geography"
                        hint="Where you would like to source from"
                        example={EX.geography}
                      >
                        <input
                          id="geography"
                          name="geography"
                          value={form.geography}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-geography"
                          placeholder="e.g. EU, India, South America"
                        />
                      </Field>
                      <Field
                        label="Volume Range"
                        htmlFor="volume_range"
                        example={EX.volume_range}
                      >
                        <input
                          id="volume_range"
                          name="volume_range"
                          value={form.volume_range}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-volume-range"
                          placeholder="e.g. 5–20 MT / year"
                        />
                      </Field>
                      <Field
                        label="Timeline"
                        htmlFor="timeline"
                        example={EX.timeline}
                      >
                        <input
                          id="timeline"
                          name="timeline"
                          value={form.timeline}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-timeline"
                          placeholder="e.g. Q1 next year"
                        />
                      </Field>
                      <div className="md:col-span-2">
                        <Field
                          label="Required Certifications"
                          htmlFor="certifications"
                          hint="Select the primary requirement — note others in the message below"
                          example={EX.certifications}
                        >
                          <select
                            id="certifications"
                            name="certifications"
                            value={form.certifications}
                            onChange={handleChange}
                            className={inputCls}
                            data-testid="intake-certifications"
                          >
                            <option value="">Select primary requirement</option>
                            {CERTIFICATIONS.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div data-testid="supplier-section" className="space-y-6">
                    <SectionTitle index={2}>Your Offering</SectionTitle>
                    <PlaybookCallout audience="supplier" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Field
                        label="Ingredient / Category"
                        htmlFor="ingredient_category"
                        example={EX.ingredient_category}
                      >
                        <input
                          id="ingredient_category"
                          name="ingredient_category"
                          value={form.ingredient_category}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-ingredient-category"
                          placeholder="e.g. Curcumin 95%, Whey Protein Isolate"
                        />
                      </Field>
                      <Field
                        label="Form"
                        htmlFor="ingredient_form"
                        example={EX.ingredient_form}
                      >
                        <select
                          id="ingredient_form"
                          name="ingredient_form"
                          value={form.ingredient_form}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-ingredient-form"
                        >
                          <option value="">Select form</option>
                          {INGREDIENT_FORMS.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field
                        label="Target Buyer Market"
                        htmlFor="application_area"
                        example={EX.application_area}
                      >
                        <select
                          id="application_area"
                          name="application_area"
                          value={form.application_area}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-application-area"
                        >
                          <option value="">Select target market</option>
                          {APPLICATIONS.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field
                        label="Markets Served / Targeted"
                        htmlFor="markets_served"
                        hint="Geographies you sell into or want to enter"
                        example={EX.markets_served}
                      >
                        <input
                          id="markets_served"
                          name="markets_served"
                          value={form.markets_served}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-markets-served"
                          placeholder="e.g. North America, EU, GCC"
                        />
                      </Field>
                      <Field
                        label="Annual Capacity"
                        htmlFor="capacity_range"
                        example={EX.capacity_range}
                      >
                        <input
                          id="capacity_range"
                          name="capacity_range"
                          value={form.capacity_range}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-capacity-range"
                          placeholder="e.g. 500 MT / year"
                        />
                      </Field>
                      <Field
                        label="Minimum Order Quantity (MOQ)"
                        htmlFor="moq"
                        example={EX.moq}
                      >
                        <input
                          id="moq"
                          name="moq"
                          value={form.moq}
                          onChange={handleChange}
                          className={inputCls}
                          data-testid="intake-moq"
                          placeholder="e.g. 250 kg"
                        />
                      </Field>
                      <div className="md:col-span-2">
                        <Field
                          label="Certifications Held"
                          htmlFor="certifications"
                          hint="Select primary — list others in the message below"
                          example={EX.certifications}
                        >
                          <select
                            id="certifications"
                            name="certifications"
                            value={form.certifications}
                            onChange={handleChange}
                            className={inputCls}
                            data-testid="intake-certifications"
                          >
                            <option value="">Select primary certification</option>
                            {CERTIFICATIONS.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section 3 — Goals & Interest */}
                <div className="space-y-6">
                  <SectionTitle index={3}>Goals & Interest</SectionTitle>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field
                      label="Primary Goal"
                      htmlFor="primary_goal"
                      example={EX.primary_goal}
                    >
                      <select
                        id="primary_goal"
                        name="primary_goal"
                        value={form.primary_goal}
                        onChange={handleChange}
                        className={inputCls}
                        data-testid="intake-primary-goal"
                      >
                        <option value="">What are you looking to achieve?</option>
                        {(isBuyer ? BUYER_GOALS : SUPPLIER_GOALS).map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field
                      label="Program Interest"
                      htmlFor="program_interest"
                      example={EX.program_interest}
                    >
                      <select
                        id="program_interest"
                        name="program_interest"
                        value={form.program_interest}
                        onChange={handleChange}
                        className={inputCls}
                        data-testid="intake-program-interest"
                      >
                        <option value="">Which offering interests you?</option>
                        {(isBuyer ? BUYER_PROGRAM : SUPPLIER_PROGRAM).map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <div className="md:col-span-2">
                      <Field
                        label="Anything else you would like us to know?"
                        htmlFor="additional_notes"
                        example={EX.additional_notes}
                      >
                        <textarea
                          id="additional_notes"
                          name="additional_notes"
                          value={form.additional_notes}
                          onChange={handleChange}
                          rows={5}
                          className={`${inputCls} resize-y`}
                          data-testid="intake-additional-notes"
                          placeholder={
                            isBuyer
                              ? "Context, current suppliers, constraints, target spec..."
                              : "Capabilities, key references, what makes your offer differentiated..."
                          }
                        />
                      </Field>
                    </div>
                  </div>
                </div>

                {/* Section 4 — Upload */}
                <div className="space-y-6">
                  <SectionTitle index={4}>Upload (Optional)</SectionTitle>
                  <Field
                    label="Supporting Materials"
                    htmlFor="file_upload"
                    hint="Coming soon — reference any documents you have and we will request them on follow-up. All submissions are treated as strictly confidential."
                  >
                    <div
                      data-testid="intake-file-upload-placeholder"
                      className="flex items-center gap-3 border border-dashed border-brand-muted/40 px-4 py-5 text-sm text-brand-quiet bg-brand-linen/70"
                    >
                      <Upload
                        className="w-4 h-4 text-brand-quiet"
                        strokeWidth={1.5}
                      />
                      File upload coming soon (PDF, DOCX, PPTX)
                    </div>
                  </Field>
                </div>

                {/* Submit */}
                <div className="pt-8 border-t border-brand-border space-y-6">
                  <label
                    htmlFor="consent"
                    data-testid="intake-consent-label"
                    className={`flex items-start gap-3 cursor-pointer p-4 border transition-colors ${
                      errors.consent
                        ? "border-red-700/60 bg-red-50/40"
                        : "border-brand-border bg-brand-ivory hover:border-brand-olive/50"
                    }`}
                  >
                    <input
                      id="consent"
                      name="consent"
                      type="checkbox"
                      checked={form.consent}
                      onChange={handleChange}
                      data-testid="intake-consent-checkbox"
                      className="mt-1 h-4 w-4 flex-shrink-0 accent-brand-olive cursor-pointer"
                    />
                    <span
                      className="text-sm text-brand-deep leading-relaxed font-light"
                      data-testid="intake-consent-text"
                    >
                      {CONSENT_TEXT}
                    </span>
                  </label>
                  {errors.consent && (
                    <p
                      data-testid="intake-consent-error"
                      className="-mt-3 text-xs text-red-700"
                    >
                      {errors.consent}
                    </p>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <div className="flex items-start gap-3 text-xs text-brand-quiet max-w-md leading-relaxed">
                      <Lock
                        className="w-4 h-4 text-brand-quiet mt-0.5 flex-shrink-0"
                        strokeWidth={1.5}
                      />
                      <span>
                        All inquiries are strictly confidential and reviewed by
                        FFIB analysts.
                      </span>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="intake-submit-button"
                      className="inline-flex items-center justify-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream px-7 py-3.5 text-sm font-medium tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Submitting..." : submitLabel}
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
